import { cn } from "@/lib/utils";
import {
    useInsertMixContentViewMutation,
    usePopularContentsLast24HoursQuery
} from "graphql/generated";
import { useAuth } from "hooks/useAuth";
import { useEffect, useRef, useState } from "react";
import { zIndex } from "styles/zIndex";

import { IconBox } from "../IconBox";

type TrendingSearch = {
    id: number;
    term: string;
    rank: number;
    contentid: string;
};

export default function TrendingContentsBanner() {
    const [trendingSearches, setTrendingSearches] = useState<TrendingSearch[]>(
        []
    );
    const [visibleIndex, setVisibleIndex] = useState(0);
    const [showAll, setShowAll] = useState(false);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    // 애니메이션 키를 변경하기 위한 state
    const [animationKey, setAnimationKey] = useState(0);
    // 컨테이너 참조 생성
    const containerRef = useRef<HTMLDivElement>(null);

    const { me } = useAuth();

    // contentView mutation 추가
    const [insertContentView] = useInsertMixContentViewMutation();

    // contentView 추가 핸들러
    const handleContentView = async (contentid: string) => {
        try {
            await insertContentView({
                variables: {
                    object: {
                        contentid,
                        userid: me?.id
                    }
                }
            });
        } catch (error) {
            console.error("ContentView 추가 실패:", error);
        }
    };

    // 실시간 인기 콘텐츠 데이터 가져오기
    const {
        data: popularContentsData,
        loading,
        error
    } = usePopularContentsLast24HoursQuery({
        variables: { limit: 10 },
        pollInterval: 600000 // 10분마다 데이터 갱신
    });

    // 인기 콘텐츠 데이터가 업데이트되면 trendingSearches 상태 업데이트
    useEffect(() => {
        if (error) {
            console.error("GraphQL 에러:", error);
            return;
        }

        if (loading) {
            return;
        }

        if (popularContentsData?.mix_content_popular_last_24h_view) {
            const updatedTrendingSearches =
                popularContentsData.mix_content_popular_last_24h_view.map(
                    (item, index) => ({
                        id: index + 1,
                        term: item.content?.title || "",
                        rank: index + 1,
                        contentid: item.contentid || ""
                    })
                );
            setTrendingSearches(updatedTrendingSearches);
        } else {
            console.log("데이터가 없습니다:", popularContentsData);
        }
    }, [popularContentsData, loading, error]);

    // 3초마다 표시되는 인덱스를 회전시키는 함수
    useEffect(() => {
        if (!showAll) {
            intervalRef.current = setInterval(() => {
                setVisibleIndex(
                    (prevIndex) => (prevIndex + 1) % trendingSearches.length
                );
                // 애니메이션 키 변경으로 애니메이션 트리거
                setAnimationKey((prev) => prev + 1);
            }, 3000);
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [showAll, trendingSearches.length]);

    // 로딩 중이거나 에러가 있는 경우 초기 데이터 표시
    if (loading || error) {
        return null;
    }

    // 빈 배열 체크 및 기본값 제공
    if (trendingSearches.length === 0) {
        return null;
    }

    // 현재 표시할 항목
    const currentItem =
        trendingSearches[visibleIndex % trendingSearches.length];

    // currentItem이 없는 경우 처리
    if (!currentItem) {
        return null;
    }

    return (
        <div
            ref={containerRef}
            className={cn(
                "relative m-0 hidden w-full max-w-[276px] md:block",
                "z-[var(--z-index-sub-header)]"
            )}
            style={{ zIndex: zIndex.subHeader }}
            onMouseEnter={() => {
                setShowAll(true);
                if (intervalRef.current) {
                    clearInterval(intervalRef.current);
                }
            }}
            onMouseLeave={() => {
                setShowAll(false);
                intervalRef.current = setInterval(() => {
                    setVisibleIndex(
                        (prevIndex) => (prevIndex + 1) % trendingSearches.length
                    );
                    // 애니메이션 키 변경으로 애니메이션 트리거
                    setAnimationKey((prev) => prev + 1);
                }, 3000);
            }}
        >
            {/* 단일 항목 - hover시 사라짐 */}
            {!showAll ? (
                <div className="flex items-center gap-[6px] overflow-hidden rounded-medium bg-grey-100 px-3 py-[6px] text-text-primary">
                    {/* 내부 콘텐츠를 감싸는 div - 애니메이션 적용 */}
                    <div
                        key={animationKey}
                        className="animate-text-slide-up flex w-full items-center gap-[6px]"
                    >
                        <div className="flex h-6 w-[15px] items-center justify-center text-[14px] font-normal leading-[22px] text-text-primary">
                            {currentItem.rank}
                        </div>
                        <div className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-[13px] font-normal leading-5">
                            {currentItem.term}
                        </div>
                    </div>
                </div>
            ) : (
                // 호버시 모든 항목 표시
                <div
                    className="absolute left-0 top-[-16px] max-h-[357px] w-full overflow-y-auto rounded-medium bg-grey-100 shadow-[0_4px_8px_rgba(0,0,0,0.1)]"
                    style={{ zIndex: zIndex.categoryPicker }}
                >
                    {/* 실시간 인기 콘텐츠 헤더 */}
                    <div className="flex items-end gap-1 border-b border-grey-200 px-3 py-[5px] text-[14px] font-bold leading-[22px] text-primary-main">
                        <IconBox
                            name="Fire"
                            size="small"
                            className="text-primary-main"
                        />
                        실시간 인기 콘텐츠
                    </div>

                    {/* 나머지 항목들 (아래로 확장) */}
                    <div>
                        {trendingSearches
                            .sort((a, b) => a.rank - b.rank)
                            .map((search) => (
                                <a
                                    key={search.id}
                                    href={
                                        process.env.BUILD_TARGET === "extension"
                                            ? `https://mix.day/contents/${search.contentid}`
                                            : `/contents/${search.contentid}`
                                    }
                                    target="_blank"
                                    rel="noreferrer"
                                    onClick={(e) => {
                                        handleContentView(search.contentid);
                                    }}
                                    className="flex items-center gap-[6px] px-3 py-1 text-text-primary no-underline hover:bg-grey-300"
                                >
                                    <div className="flex h-6 w-[15px] items-center justify-center text-[14px] font-normal leading-[22px] text-text-primary">
                                        {search.rank}
                                    </div>
                                    <div className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-[13px] font-normal leading-5">
                                        {search.term}
                                    </div>
                                </a>
                            ))}
                    </div>
                </div>
            )}
        </div>
    );
}
